import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import '../components/ui/ui-button';
import { navigateBack } from '../router';
import { cardStyles, motionStyles, pageFrameStyles } from '../styles/lit-shared';

const SKILLS = [
  { icon: '🎨', title: 'Composición visual', desc: 'Entiende prompts complejos y los convierte en imágenes con composición cuidada, no en collages sin criterio.' },
  { icon: '🖼️', title: 'Resolución lista para usar', desc: 'Genera directamente a 1920×1080. Lo que sale está listo para publicar en web o redes sin retoques de tamaño.' },
  { icon: '🎯', title: 'Coherencia con el contenido', desc: 'No inventa por su cuenta. La imagen siempre parte del prompt del Director, que ya sabe de qué trata el artículo.' },
  { icon: '⚡', title: 'Velocidad razonable', desc: 'Una imagen profesional en 1-2 minutos. No es instantáneo, pero tampoco es el banco de imágenes de siempre.' },
];

@customElement('agent-artist-page')
export class AgentArtistPage extends LitElement {
  private goBack() {
    navigateBack('/arquitectura');
  }

  static styles = [
    pageFrameStyles,
    cardStyles,
    motionStyles,
    css`
      .back-btn {
        margin-bottom: var(--space-xl);
      }

      .agent-header {
        margin-bottom: var(--space-2xl);
      }

      .agent-emoji {
        font-size: 3rem;
        line-height: 1;
        margin-bottom: var(--space-md);
        display: block;
      }

      .agent-title {
        font-size: clamp(2rem, 5vw, 2.75rem);
        font-weight: 700;
        letter-spacing: -0.03em;
        margin: 0 0 var(--space-sm);
        color: var(--color-text);
      }

      .agent-tagline {
        font-size: 1.125rem;
        color: var(--color-accent);
        font-weight: 500;
        margin: 0;
      }

      .section-title {
        font-size: 1.25rem;
        font-weight: 700;
        letter-spacing: -0.02em;
        margin: 0 0 var(--space-md);
        color: var(--color-text);
      }

      .what-section {
        margin-bottom: var(--space-2xl);
      }

      .what-section p {
        color: var(--color-text-muted);
        font-size: 1.0625rem;
        line-height: 1.7;
        margin: 0;
        max-width: 64ch;
      }

      .skills-grid {
        display: grid;
        gap: var(--space-md);
        grid-template-columns: 1fr;
        margin-bottom: var(--space-2xl);
      }

      @media (min-width: 640px) {
        .skills-grid {
          grid-template-columns: repeat(2, 1fr);
        }
      }

      .skill-card {
        display: flex;
        gap: var(--space-md);
        align-items: flex-start;
      }

      .skill-icon {
        font-size: 1.5rem;
        flex-shrink: 0;
        line-height: 1;
        margin-top: 0.1rem;
      }

      .skill-card h3 {
        margin: 0 0 0.25rem;
        font-size: 0.9375rem;
        font-weight: 600;
        color: var(--color-text);
      }

      .skill-card p {
        margin: 0;
        font-size: 0.875rem;
        color: var(--color-text-muted);
        line-height: 1.55;
      }

      .flow-section {
        margin-bottom: var(--space-2xl);
      }

      .flow-steps {
        display: grid;
        gap: var(--space-md);
      }

      .step {
        display: flex;
        gap: var(--space-md);
        align-items: flex-start;
      }

      .step-num {
        flex-shrink: 0;
        width: 2rem;
        height: 2rem;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: var(--radius-full);
        background: var(--gradient-primary);
        color: var(--palette-white);
        font-weight: 700;
        font-size: 0.875rem;
      }

      .step h3 {
        margin: 0 0 0.25rem;
        font-size: 1rem;
        font-weight: 600;
      }

      .step p {
        margin: 0;
        color: var(--color-text-muted);
        font-size: 0.9375rem;
        line-height: 1.55;
      }

      .model-note {
        padding: var(--space-lg);
        border-radius: var(--radius);
        background: var(--gradient-primary-soft);
        border: 1px solid var(--color-border-glow);
        font-size: 0.9375rem;
        color: var(--color-text-muted);
        line-height: 1.6;
        margin-bottom: var(--space-xl);
      }

      .model-note strong {
        color: var(--color-text);
      }
    `,
  ];

  render() {
    return html`
      <div class="animate-fade-up">
        <div class="back-btn">
          <ui-button variant="ghost" @ui-click=${this.goBack}>← Volver</ui-button>
        </div>

        <div class="agent-header">
          <span class="agent-emoji">🖼️</span>
          <h1 class="agent-title">El Artista</h1>
          <p class="agent-tagline">El último en actuar. Convierte el brief del Director en una imagen que acompaña al contenido.</p>
        </div>

        <div class="what-section">
          <h2 class="section-title">¿Qué hace?</h2>
          <p>
            Cuando el Director da el visto bueno al contenido, también genera un prompt
            visual: una descripción detallada de qué imagen tiene que crear el Artista.
            No es un "genera algo bonito" — es una instrucción específica sobre composición,
            colores, estilo y qué tiene que transmitir. El Artista la convierte en una imagen
            de 1920×1080, lista para usar en el artículo o en redes. Sin stock photos, sin
            señores sonriendo señalando gráficos.
          </p>
        </div>

        <div>
          <h2 class="section-title">Sus armas</h2>
          <div class="skills-grid">
            ${SKILLS.map(
              (s) => html`
                <article class="card skill-card">
                  <span class="skill-icon">${s.icon}</span>
                  <div>
                    <h3>${s.title}</h3>
                    <p>${s.desc}</p>
                  </div>
                </article>
              `,
            )}
          </div>
        </div>

        <div class="flow-section">
          <h2 class="section-title">Cómo actúa</h2>
          <div class="flow-steps">
            <div class="step">
              <span class="step-num">1</span>
              <div>
                <h3>Recibe el prompt del Director</h3>
                <p>No trabaja a ciegas. El Director le entrega una descripción específica basada en el contenido generado: qué tiene que transmitir, qué estilo, qué colores.</p>
              </div>
            </div>
            <div class="step">
              <span class="step-num">2</span>
              <div>
                <h3>Genera la imagen</h3>
                <p>En 1-2 minutos produce una imagen a 1920×1080. Sin ajustes manuales de tu parte, sin selección de plantilla, sin banco de imágenes que no encajan.</p>
              </div>
            </div>
            <div class="step">
              <span class="step-num">3</span>
              <div>
                <h3>Entrega lista para usar</h3>
                <p>La imagen llega junto al resto del contenido. Úsala tal cual o como punto de partida — pero al menos no empiezas desde cero.</p>
              </div>
            </div>
          </div>
        </div>

        <div class="model-note">
          <strong>Modelo: Stable Diffusion XL</strong> — Especializado en generación de imágenes
          de alta calidad. Bueno en composición, varios estilos visuales y coherencia entre
          prompt e imagen. A veces puede tener pequeños artefactos en los detalles, pero para
          imágenes de cabecera y contenido de blog funciona muy bien.
        </div>

        <ui-button href="https://blunticle.alejandrosn.com" variant="primary">Probar Blunticle</ui-button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'agent-artist-page': AgentArtistPage;
  }
}
